import React, { useEffect, useState } from 'react';
import { FileCheck2, FileSignature, FileEdit, FilePlus, GraduationCap, CalendarX, CalendarCheck, UserPlus, ClipboardCheck, ListChecks, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../utils/api';
import { submitKeysOf } from '../components/workflow/useWorkflowPerms';
import HubCard, { HubCardGrid, HubSectionHeader } from '../components/shared/HubCard';

// Workflow hub — sectioned card index of firm process workflows.
// Cards carry a permission (string) or permission array (any one shows the
// card): the flow's submit keys (submitKeysOf — "new" and "amendment" are
// separate keys, Signed Financial Statements / QC35 Form the Audit ones) OR
// its own review key — a pure reviewer must still reach their queue.
// `chosenReviewer` cards (no review key) also show to anyone ever named as a
// reviewer in that flow (GET /api/workflow/named-flows) or with a request
// awaiting them — the named reviewer needs no key at all.
// (docs/workflow-ui.md §5, docs/modules/workflow-ld-cpd.md).
// Cards appear when the user holds the matching permission; `permission: null`
// means every logged-in user (self-service flows). A section with no visible
// cards is hidden entirely.
// `iconClass` is the section's icon colour, matched to the Management hub's
// sections: client grey, screening teal, engagement (and its amendments) blue,
// professional development green.
const SECTIONS = [
  {
    title: 'Client',
    description: 'Register and update clients.',
    iconClass: 'bg-slate-100 text-slate-600',
    workflows: [
      {
        name: 'New Client',
        href: '/workflow/client',
        permission: submitKeysOf('client'),
        chosenReviewer: true,
        countKey: 'client',
        icon: UserPlus,
        description: 'Register a new client.',
      },
      {
        name: 'Client Amendment',
        href: '/workflow/car',
        permission: submitKeysOf('car'),
        chosenReviewer: true,
        countKey: 'car',
        icon: FileEdit,
        description: "Update a registered client's details.",
      },
    ],
  },
  {
    // Screening is its own workflow (owner 2026-09-11) — not a step of the
    // engagement pipeline, even though an approved registration can open one.
    title: 'Screening',
    description: "QM background checks.",
    iconClass: 'bg-teal-50 text-teal-600',
    workflows: [
      {
        name: 'Screening',
        href: '/workflow/screening',
        permission: ['workflow:screening:submit', 'workflow:screening:review'],
        countKey: 'screening',
        icon: ShieldCheck,
        description: 'Request a QM background check.',
      },
    ],
  },
  {
    title: 'Engagement',
    description: 'From registration to file archival.',
    iconClass: 'bg-blue-50 text-blue-600',
    workflows: [
      {
        name: 'Engagement Registration',
        href: '/workflow/engreg',
        permission: submitKeysOf('engreg'),
        chosenReviewer: true,
        countKey: 'engreg',
        icon: FileCheck2,
        description: 'Register the engagements of a new project.',
      },
      {
        name: 'QC6 / QC7 Forms',
        href: '/workflow/qc',
        permission: submitKeysOf('qc'),
        chosenReviewer: true,
        countKey: 'qc',
        icon: ClipboardCheck,
        description: 'Client acceptance (QC6) or continuance (QC7).',
      },
      {
        name: 'QC6 / QC7 Addendum',
        href: '/workflow/qcad',
        permission: submitKeysOf('qcad'),
        chosenReviewer: true,
        countKey: 'qcad',
        icon: FilePlus,
        description: 'Information identified after a QC6 / QC7 was approved.',
      },
      {
        name: 'CaseWare, Charge Code & Team Update',
        href: '/workflow/cwcc',
        permission: [...submitKeysOf('cwcc'), 'workflow:cwcc:review', 'workflow:cwcc:complete'],
        countKey: 'cwcc',
        icon: FileCheck2,
        description: 'Request CaseWare access, a charge code or a team update.',
      },
      {
        name: 'Signed Financial Statements',
        href: '/workflow/fs',
        permission: submitKeysOf('fs'),
        chosenReviewer: true,
        countKey: 'fs',
        icon: FileSignature,
        description: 'Upload the signed financial statements.',
      },
      {
        name: 'QC35 Form',
        href: '/workflow/qc35',
        permission: submitKeysOf('qc35'),
        chosenReviewer: true,
        countKey: 'qc35',
        icon: FileSignature,
        description: 'Complete the QC35 Form file archival checklist.',
      },
    ],
  },
  {
    title: 'Engagement — Amendment',
    description: 'Correct approved records.',
    iconClass: 'bg-blue-50 text-blue-600',
    workflows: [
      {
        name: 'Engagement Amendment',
        href: '/workflow/ear',
        permission: submitKeysOf('ear'),
        chosenReviewer: true,
        countKey: 'ear',
        icon: FileEdit,
        description: 'Change the details of an existing engagement.',
      },
      {
        name: 'QC Amendment',
        href: '/workflow/qca',
        permission: submitKeysOf('qca'),
        chosenReviewer: true,
        countKey: 'qca',
        icon: FileEdit,
        description: 'Correct an approved QC6 / QC7 record.',
      },
      {
        name: 'Engagement Status Change',
        href: '/workflow/esc',
        permission: [...submitKeysOf('esc'), 'workflow:esc:review'],
        countKey: 'esc',
        icon: ListChecks,
        description: "Request a change to an engagement's status.",
      },
      {
        name: 'Signed Financial Statements Amendment',
        href: '/workflow/fsa',
        permission: submitKeysOf('fsa'),
        chosenReviewer: true,
        countKey: 'fsa',
        icon: FileEdit,
        description: 'Correct approved signed financial statements.',
      },
      {
        name: 'QC35 Form Amendment',
        href: '/workflow/qc35a',
        permission: submitKeysOf('qc35a'),
        chosenReviewer: true,
        countKey: 'qc35a',
        icon: FileEdit,
        description: 'Correct an approved QC35 Form.',
      },
    ],
  },
  {
    title: 'Professional Development',
    description: 'Learning & development requests, reviewed by HR / L&D.',
    iconClass: 'bg-emerald-50 text-emerald-600',
    workflows: [
      {
        name: 'External CPD',
        href: '/workflow/cpd',
        permission: ['ld:submit', 'ld:cpd:review'], // ld:submit gates declarations since 0112
        countKey: 'cpd',
        icon: GraduationCap,
        description: 'Declare an external course you attended.',
      },
      {
        name: 'Professional Qualification',
        href: '/workflow/pq',
        permission: ['ld:submit', 'ld:pq:review'],
        countKey: 'pq',
        icon: GraduationCap,
        description: 'Declare a professional qualification you hold.',
      },
      {
        name: 'Training Absence',
        href: '/workflow/absence',
        permission: ['ld:submit', 'ld:absence:review'],
        countKey: 'absence',
        icon: CalendarX,
        description: 'Tell HR why you missed a training session.',
      },
      {
        name: 'Attendance Dispute',
        href: '/workflow/dispute',
        permission: ['ld:submit', 'ld:absence:review'],
        countKey: 'dispute',
        icon: CalendarCheck,
        description: 'Dispute a session marked as missed.',
      },
    ],
  },
];

const WorkflowHub = () => {
  const { hasPermission } = useAuth();

  // Per-workflow pending-review counts so reviewers see which card has items
  // awaiting them. The count endpoints return 0/null for non-reviewers, so the
  // badge simply never appears for submitters.
  // ONE feed for every queue, L&D included (docs/workflow-contract.md §2,
  // 接點 4) — no per-flow fan-out.
  const [counts, setCounts] = useState({
    client: 0, car: 0, engreg: 0, screening: 0, qc: 0, cwcc: 0, esc: 0, fs: 0, qc35: 0, ear: 0, qca: 0, qcad: 0, fsa: 0, qc35a: 0,
    cpd: 0, pq: 0, absence: 0, dispute: 0,
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/workflow/pending-count`, { credentials: 'include' });
        const wf = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          setCounts({
            client: wf.client || 0,
            car: wf.car || 0,
            engreg: wf.engreg || 0,
            screening: wf.screening || 0,
            qc: wf.qc || 0,
            cwcc: wf.cwcc || 0,
            esc: wf.esc || 0,
            fs: wf.fs || 0,
            qc35: wf.qc35 || 0,
            ear: wf.ear || 0,
            qca: wf.qca || 0,
            qcad: wf.qcad || 0,
            fsa: wf.fsa || 0,
            qc35a: wf.qc35a || 0,
            cpd: wf.cpd || 0,
            pq: wf.pq || 0,
            absence: wf.absence || 0,
            dispute: wf.dispute || 0,
          });
        }
      } catch (e) { /* badges are best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Chosen-reviewer flows the caller has been named in — their cards stay
  // reachable without any workflow key.
  const [namedFlows, setNamedFlows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/workflow/named-flows`, { credentials: 'include' });
        const body = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setNamedFlows(body.flows || []);
      } catch (e) { /* the key-based cards still show */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = (w) => !w.permission
    || (Array.isArray(w.permission) ? w.permission.some(hasPermission) : hasPermission(w.permission))
    || (w.chosenReviewer && (namedFlows.includes(w.countKey) || (counts[w.countKey] || 0) > 0));

  const sections = SECTIONS
    .map(s => ({
      ...s,
      workflows: s.workflows.filter(visible),
    }))
    .filter(s => s.workflows.length > 0);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Workflow</h1>
        <p className="text-sm text-slate-500 mt-1">Submit and track firm process requests.</p>
      </div>

      {sections.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500 text-sm">
          You do not have access to any workflows yet — ask an administrator to grant workflow permissions.
        </div>
      ) : (
        <div className="space-y-8">
          {sections.map(({ title, description, iconClass, workflows }) => (
            <section key={title}>
              <HubSectionHeader title={title} description={description} />
              <HubCardGrid>
                {workflows.map(({ name, href, icon, description: desc, countKey }) => {
                  const pending = (countKey && counts[countKey]) || 0;
                  return (
                    <HubCard
                      key={href}
                      to={href}
                      icon={icon}
                      name={name}
                      description={desc}
                      iconClass={iconClass}
                      badge={pending > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                          {pending} to review
                        </span>
                      ) : null}
                    />
                  );
                })}
              </HubCardGrid>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowHub;
